import React, { useState, useEffect, useMemo } from 'react';
import { FaPlus, FaEdit, FaTrash, FaCertificate, FaGraduationCap, FaSearch, FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../../api/apiClient';
import { Modal, Row, Col, Button, Spinner } from 'react-bootstrap';


const CourseAssignmentsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    course_id: '',
    certificate_template_id: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const initialTemplateId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('templateId') || '';
  }, [location.search]);
  const [templateFilter, setTemplateFilter] = useState(initialTemplateId);

  useEffect(() => {
    setTemplateFilter(initialTemplateId);
    setCurrentPage(1);
  }, [initialTemplateId]);

  useEffect(() => {
    fetchAssignments();
  }, [currentPage, searchTerm, templateFilter]);

  useEffect(() => {
    fetchCourses();
    fetchTemplates();
  }, []);

  const fetchAssignments = async () => {
    if (!templateFilter) {
      setAssignments([]);
      setTotalPages(1);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.certificates.getAllAssignments({
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...(templateFilter ? { template_id: templateFilter } : {})
      });

      if (response.data.success) {
        setAssignments(response.data.data.assignments);
        setTotalPages(response.data.data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
      toast.error('Failed to fetch course assignments');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await api.courses.getAll(true);
      if (response.data.success) {
        const coursesData = response.data.data.courses || response.data.data || response.data.courses;
        setCourses(coursesData);
      } else {
        console.error('Failed to fetch courses:', response.data.message);
        toast.error('Failed to fetch courses');
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to fetch courses');
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.certificates.getTemplates({ limit: 100 });
      if (response.data.success) {
        setTemplates(response.data.data.templates);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleTemplateChange = (e) => {
    const value = e.target.value;
    setTemplateFilter(value);
    setCurrentPage(1);
    if (value) {
      navigate(`/admin/certificates/assignments?templateId=${value}`);
    } else {
      navigate('/admin/certificates/assignments');
    }
  };

  const handleShowModal = (mode, assignment = null) => {
    setModalMode(mode);
    setSelectedAssignment(assignment);
    
    if (mode === 'edit' && assignment) {
      setFormData({
        course_id: assignment.course_id,
        certificate_template_id: assignment.certificate_template_id
      });
    } else {
      setFormData({
        course_id: '',
        certificate_template_id: templateFilter || ''
      });
    }
    
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAssignment(null);
    setFormData({
      course_id: '',
      certificate_template_id: templateFilter || ''
    });
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.course_id) {
      errors.course_id = 'Please select a course';
    }
    
    if (!formData.certificate_template_id) {
      errors.certificate_template_id = 'Please select a certificate template';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      let response;
      if (modalMode === 'create') {
        response = await api.certificates.assignToCourse(formData);
      } else {
        response = await api.certificates.updateAssignment(selectedAssignment.id, formData);
      }
      
      if (response.data.success) {
        toast.success(`Certificate ${modalMode === 'create' ? 'assigned' : 'updated'} successfully`);
        handleCloseModal();
        fetchAssignments();
      }
    } catch (error) {
      console.error('Error saving assignment:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(`Failed to ${modalMode} assignment`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnassignClick = (assignment) => {
    setAssignmentToDelete(assignment);
    setShowDeleteModal(true);
  };

  const handleUnassignConfirm = async () => {
    if (!assignmentToDelete) return;

    setDeleteLoading(true);
    try {
      const response = await api.certificates.unassignFromCourse(assignmentToDelete.id);
      if (response.data.success) {
        toast.success('Certificate unassigned successfully');
        setShowDeleteModal(false);
        setAssignmentToDelete(null);
        fetchAssignments();
      }
    } catch (error) {
      console.error('Error unassigning certificate:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to unassign certificate');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleUnassignCancel = () => {
    setShowDeleteModal(false);
    setAssignmentToDelete(null);
    setDeleteLoading(false);
  };

  const filteredAssignments = useMemo(() => {
    if (!templateFilter) {
      return assignments;
    }

    const templateIdAsString = `${templateFilter}`;

    return assignments.filter(assignment => {
      const directMatch = `${assignment.certificate_template_id}` === templateIdAsString;
      const nestedMatch = assignment.template && `${assignment.template.id}` === templateIdAsString;
      return directMatch || nestedMatch;
    });
  }, [assignments, templateFilter]);

  const getAvailableCourses = () => {
    if (!templateFilter) {
      return [];
    }

    if (modalMode === 'edit') {
      return courses || [];
    }
    
    if (!courses || !filteredAssignments) {
      return [];
    }
    
    const assignedCourseIds = filteredAssignments.map(a => a.course_id);
    return courses.filter(course => !assignedCourseIds.includes(course.id));
  };

  const selectedTemplate = useMemo(() => {
    if (!templateFilter) return null;
    return templates.find(template => `${template.id}` === `${templateFilter}`);
  }, [templateFilter, templates]);

  const headerDescription = selectedTemplate
    ? `Assignments linked to "${selectedTemplate.name}"`
    : 'Select a certificate template to view its course assignments.';

  const availableCourseCount = useMemo(() => {
    const list = getAvailableCourses();
    return list ? list.length : 0;
  }, [courses, filteredAssignments, modalMode, templateFilter]);

  const showAssignments = Boolean(templateFilter);

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/certificates/templates')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 font-medium"
          >
            <FaArrowLeft className="w-4 h-4" /> Back
          </button>
          <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
            Course Certificate Assignments
          </h5>
        </div>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="lg:col-span-2">
          <div className="flex shadow-sm rounded-lg sm:rounded-xl overflow-hidden bg-white">
            <div className="flex items-center px-3 bg-white border-r border-gray-200">
              <FaSearch className="w-4 h-4 text-purple-600" />
            </div>
            <input
              type="text"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base outline-none"
            />
          </div>
        </div>
        <select
          value={templateFilter}
          onChange={handleTemplateChange}
          className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-sm form-select"
        >
          <option value="">Select Template</option>
          {templates.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <button
          onClick={() => handleShowModal('create')}
          disabled={!showAssignments || availableCourseCount === 0}
          className="bg-white border border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
        >
          <FaPlus className="w-4 h-4" />
          <span className="whitespace-nowrap">Assign Certificate</span>
        </button>
      </div>

      {/* No Template Selected */}
      {!showAssignments ? (
        <div className="text-center py-12 rounded-2xl bg-white/80 backdrop-blur shadow-md border border-purple-100">
          <FaCertificate className="w-20 h-20 text-purple-300 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium text-purple-700">Select a certificate template to view assignments</p>
        </div>
      ) : loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
        </div>
      ) : filteredAssignments.length === 0 ? (
        <div className="text-center py-12 rounded-2xl bg-white/80 backdrop-blur shadow-md border border-purple-100">
          <p className="text-lg font-medium text-purple-700">No assignments found for this template</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl shadow-lg bg-white">
          <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="py-2 px-3 text-left">Course</th>
                <th className="py-2 px-3 text-left">Certificate Template</th>
                <th className="py-2 px-3 text-left">Assigned By</th>
                <th className="py-2 px-3 text-left">Status</th>
                <th className="py-2 px-3 text-left">Assigned Date</th>
                <th className="py-2 px-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAssignments.map(assignment => (
                <tr key={assignment.id} className="border-b border-purple-100 hover:bg-purple-50/50 transition-all">
                  <td className="py-2 px-3">
                    <div className="font-semibold text-gray-800">{assignment.course?.name || 'Unknown'}</div>
                    {assignment.course?.description && (
                      <div className="text-gray-600 text-xs mt-1">
                        {assignment.course.description.length > 60 
                          ? `${assignment.course.description.substring(0, 60)}...` 
                          : assignment.course.description}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <div >{assignment.template?.name || 'Unknown'}</div>
                  </td>
                  <td className="py-2 px-3 text-gray-700">
                    {assignment.assignedBy 
                      ? `${assignment.assignedBy.first_name} ${assignment.assignedBy.last_name}`
                      : 'System'}
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-4 py-2 rounded-full text-xs font-bold ${assignment.status === 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                      {assignment.status === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-gray-700">
                    {new Date(assignment.createdAt).toLocaleDateString()}
                  </td>
                  <td className="py-2 px-3 text-center">
                    <div className="flex gap-3 justify-center">
                      <button
                        onClick={() => handleShowModal('edit', assignment)}
                        className="text-purple-600 hover:bg-purple-50 p-2 rounded-lg transition-all"
                        title="Edit Assignment"
                      >
                        <FaEdit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleUnassignClick(assignment)}
                        className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition-all"
                        title="Unassign Certificate"
                      >
                        <FaTrash className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal - Same as OrgUsers */}
      <div className={`fixed inset-0 z-50 flex items-center justify-center ${showModal ? 'block' : 'hidden'} bg-black bg-opacity-50`}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 modal-header">
            <div className="font-bold text-lg modal-title h4">
                {modalMode === 'create' ? 'Assign Certificate to Course' : 'Edit Assignment'}
            </div>
                          <button type="button" onClick={handleCloseModal} class="btn-close btn-close-white" aria-label="Close"></button>

          </div>
          <div className="p-2 bg-gray-50">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Course <span className="text-red-500">*</span></label>
                  <select
                    name="course_id"
                    value={formData.course_id}
                    onChange={handleInputChange}
                    disabled={modalMode === 'edit'}
                    className={`rounded-lg border-purple-200 focus:ring-purple-500 form-select ${formErrors.course_id ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm ${modalMode === 'edit' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  >
                    <option value="">Select a course</option>
                    {(modalMode === 'edit' ? courses : getAvailableCourses()).map(course => (
                      <option key={course.id} value={course.id}>{course.name}</option>
                    ))}
                  </select>
                  {formErrors.course_id && <p className="text-red-500 text-xs mt-1">{formErrors.course_id}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Certificate Template <span className="text-red-500">*</span></label>
                  <select
                    name="certificate_template_id"
                    value={formData.certificate_template_id}
                    onChange={handleInputChange}
                    disabled
                    className="rounded-lg border-purple-200 focus:ring-purple-500 form-select"
                  >
                    <option value="">{selectedTemplate?.name || 'Loading...'}</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-4 justify-center">
                          <Row className="w-100 g-2">
            <Col xs={6}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={submitting}
                  bsPrefix="custom-hamburger"
                  className="w-full bg-gray-200 text-gray-700 border border-gray-400 font-semibold rounded-lg hover:bg-gray-300 py-2"
                >
                  Cancel
                </button>
                    </Col>
            <Col xs={6}>
                <button
                  type="submit"
                  bsPrefix="custom-hamburger"
                  disabled={submitting}
                  className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {modalMode === 'create' ? 'Assigning...' : 'Updating...'}
                    </>
                  ) : (
                    modalMode === 'create' ? 'Assign Certificate' : 'Update Assignment'
                  )}
                </button>
                   </Col>
          </Row>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete/Unassign Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleUnassignCancel} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Confirm Unassign
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Unassign Certificate</h5>
          <p className="text-gray-600">
            Are you sure you want to unassign the certificate from{' '}
            <strong>{assignmentToDelete ? assignmentToDelete.course?.name : ''}</strong>?
          </p>
          <p className="text-sm text-gray-500 mt-3">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleUnassignCancel}
                disabled={deleteLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleUnassignConfirm}
                disabled={deleteLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {deleteLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Unassigning...
                  </>
                ) : (
                  'Unassign Certificate'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CourseAssignmentsPage;